import './ProductList.css';

import { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';

import { BodyText, Button, Div, Flex, GridBag, Gutter, Page } from '@gaiads/telia-react-component-library';
import {
  BrandFilterOption,
  CustomPaginator,
  PageChangeInfo,
  ProductListing,
  ProductListingPagingOptions,
  ProductListingTranslations,
  SelectedBrandFilters,
  SortingOption,
  useToBreakpoint,
} from '@cdaas/reusable-components-daas';
import { PageInfo, UserRole } from '@cdaas/api-clients/dist/asset-user-openapi';

import { RootState } from '../../../store/reducers';
import { authSetPendingApprovals } from '../../../store/actions';
import {
  checkIsSelectedOrganizationOwn,
  hasUserARequiredRole,
  isSingleDevicePolicyInEffectForUser,
  isWebshopEnabled,
  buildQueryString,
  getNumberOfPendingApprovals,
  getProductListingAvailableBrands,
  getProducts,
  NUMBER_OF_PRODUCT_PER_PAGE,
} from '../../../utils';
import { FilterProductsParams, WrapperItemOverview } from '../../../types';

import AdministratingOtherOrganization from './AdministratingOtherOrganization';
import DeviceLimitFullNotification from '../../../Components/Notification/DeviceLimitFullNotification';
import { QuestionsAndAnswers } from '../../../Components/QuestionsAndAnswers/QuestionsAndAnswers';
import OrderingOnBehalfInput from './OrderingOnBehalfInput/OrderingOnBehalfInput';

const ProductList: FunctionComponent = () => {
  const dispatch = useDispatch();
  const [products, setProducts] = useState<WrapperItemOverview[]>([]);
  const [searchString, setSearchString] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const history = useHistory();
  const location = useLocation();
  const { t, i18n } = useTranslation('product');
  const { t: tBundle } = useTranslation('bundle');
  const { t: tShop } = useTranslation('shop');
  const [pagingOptions, setPagingOptions] = useState<ProductListingPagingOptions>({
    totalPages: 1,
    totalItems: 2,
    currentPage: 1,
    numberOfProductsPerPage: NUMBER_OF_PRODUCT_PER_PAGE,
    numberOfProductsPerPageOptions: [10, 20, 30, 40],
  });
  const [brandOptions, setBrandOptions] = useState<BrandFilterOption[]>([]);
  const [selectedBrandFilters, setSelectedBrandFilters] = useState<SelectedBrandFilters>({});
  const [textSearch, setTextSearch] = useState('');
  const initialSortingOptions = useMemo(
    () => [
      { label: t('Sorting.priceAsc'), value: 'price_asc', selected: false },
      { label: t('Sorting.priceDesc'), value: 'price_desc', selected: false },
    ],
    [t],
  );

  const [pageInfo, setPageInfo] = useState<PageInfo>();
  const [sortingOptions, setSortingOptions] = useState<SortingOption[]>(initialSortingOptions);
  const [deviceLimitFullInfoDismissed, setDeviceLimitFullInfoDismissed] = useState(false);

  const orderingForOrganization = useSelector((state: RootState) => state.shop.orderForOrganization);
  const authenticatedUser = useSelector((state: RootState) => state.authentication.user);
  const orderOnBehalfPendingUserSelection = useSelector((state: RootState) => state.shop.orderOnBehalfPendingUserSelection);
  const orderOnBehalfUser = useSelector((state: RootState) => state.shop.orderOnBehalfUser);
  const ownOrganization = useSelector((state: RootState) => state.authentication.user?.organization);
  const selectedOrganization = useSelector((state: RootState) => state.authentication.selectedOrganization);

  const selectedOrganizationIsOwn = checkIsSelectedOrganizationOwn(ownOrganization?.id, selectedOrganization?.id);
  const isAdministratingSubsidiaryWithoutOrderRole = !selectedOrganizationIsOwn && !hasUserARequiredRole([UserRole.Orderer]);

  const numberOfProductColumns = useToBreakpoint(600) ? 2 : 3;
  const isMobile = useToBreakpoint(840);

  const [isMounted, setIsMounted] = useState(true);
  const [showProductList, setShowProductList] = useState(selectedOrganizationIsOwn);

  useEffect(() => {
    if (!isAdministratingSubsidiaryWithoutOrderRole && showProductList && !isWebshopEnabled(selectedOrganization, ownOrganization)) {
      getProductListingAvailableBrands().then(brands => setBrandOptions(brands));
    }
  }, [isAdministratingSubsidiaryWithoutOrderRole, selectedOrganization, ownOrganization, showProductList]);

  useEffect(() => {
    if (!location.search.length) {
      setTextSearch('');
      setSortingOptions(initialSortingOptions);
      setPagingOptions(prevPagingOptions => ({
        ...prevPagingOptions,
        currentPage: 1,
        numberOfProductsPerPage: NUMBER_OF_PRODUCT_PER_PAGE,
      }));
      setSelectedBrandFilters({});
    }
  }, [location.search, initialSortingOptions]);

  useEffect(() => {
    const checkAndIncludeUserParameter = (search: string, userId: string) => {
      const userParameter = 'user_id';
      if (!search.includes(userParameter)) {
        return search.startsWith('?') ? `${search}&${userParameter}=${userId}` : `?${userParameter}=${userId}`;
      }
      return search;
    };

    let searchString = location.search;
    if (!searchString.includes('size')) {
      searchString += searchString.length > 1 ? '&' : '?';
      searchString += `size=${NUMBER_OF_PRODUCT_PER_PAGE}`;
    }

    if (orderingForOrganization || orderOnBehalfUser.status || selectedOrganizationIsOwn) {
      if (!orderingForOrganization) {
        const userId = orderOnBehalfUser.status && orderOnBehalfUser.userToOrderFor ? orderOnBehalfUser.userToOrderFor.id : authenticatedUser?.id;
        if (userId) {
          searchString = checkAndIncludeUserParameter(searchString, userId);
        } else {
          throw new Error('User missing, unable to get products and bundles.');
        }
      }
      setSearchString(searchString);
    }
  }, [authenticatedUser, location.search, orderingForOrganization, orderOnBehalfUser, selectedOrganizationIsOwn, i18n.language]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedGetProducts = useCallback(
    debounce((search: string) => {
      setLoading(true);
      setError('');
      getProducts(search).then(data => {
        if (isMounted) {
          if (data) {
            setProducts(data.items);
            setPagingOptions(prevState => ({
              ...prevState,
              totalItems: data.page_metadata.total_items,
              totalPages: data.page_metadata.total_pages,
            }));
            setPageInfo(data.page_metadata);
          } else {
            setError(t('ProductListingComponent.getProductsErrorTitle'));
          }
          setLoading(false);
        }
      });
    }, 100),
    [isMounted, orderingForOrganization, t],
  );

  useEffect(() => {
    if (showProductList && !isWebshopEnabled(selectedOrganization, ownOrganization)) {
      debouncedGetProducts(searchString);
    }
    return () => {
      debouncedGetProducts.cancel();
    };
  }, [searchString, selectedOrganization, ownOrganization, showProductList, debouncedGetProducts]);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const name = query.get('name');
    setTextSearch(name ?? '');

    const pageQuery: string | null = query.get('page');
    const sizeQuery: string | null = query.get('size');
    if (pageQuery || sizeQuery) {
      let page: number | null = pageQuery ? parseInt(pageQuery) : null;
      page = Number.isInteger(page) ? ++page! : null;
      const size: number | null = sizeQuery ? parseInt(sizeQuery) : null;

      setPagingOptions(prevOptions => ({
        ...prevOptions,
        currentPage: page ?? prevOptions.currentPage,
        numberOfProductsPerPage: size ?? prevOptions.numberOfProductsPerPage,
      }));
    } else {
      setPagingOptions(prevOptions => ({
        ...prevOptions,
        currentPage: 1,
        numberOfProductsPerPage: NUMBER_OF_PRODUCT_PER_PAGE,
      }));
    }

    const brands: string[] = query.get('brand') ? query.get('brand')!.split(',') : [];
    const newSelectedBrandFilters: SelectedBrandFilters = {};
    brands.forEach(brand => {
      newSelectedBrandFilters[brand] = true;
    });
    setSelectedBrandFilters(newSelectedBrandFilters);

    const sort = query.get('sort');
    const sortDir = query.get('direction');
    if (sort && sortDir) {
      const sortString = `${sort}_${sortDir.toLowerCase()}`;
      setSortingOptions(prevOptions =>
        prevOptions.map(so => {
          so.selected = so.value === sortString;
          return so;
        }),
      );
    } else {
      setSortingOptions(prevSortingOptions => prevSortingOptions.map(o => ({ ...o, selected: false })));
    }
  }, [location.search]);

  useEffect(() => {
    if (selectedOrganization) {
      getNumberOfPendingApprovals().then(approvals => {
        dispatch(authSetPendingApprovals(approvals || 0));
      });
    }
  }, [dispatch, selectedOrganization]);

  useEffect(() => {
    setShowProductList(!orderOnBehalfPendingUserSelection && (selectedOrganizationIsOwn || orderingForOrganization || orderOnBehalfUser.status));
  }, [selectedOrganizationIsOwn, orderingForOrganization, orderOnBehalfUser.status, orderOnBehalfPendingUserSelection]);

  const productListingTranslations: ProductListingTranslations = {
    productsSortLabel: t('ProductListingComponent.productsSortLabel'),
    brandFilterTitle: t('ProductListingComponent.brandFilterTitle'),
    mobileSearchAccordionTitle: t('ProductListingComponent.mobileSearchAccordionTitle'),
    productPriceLabel: t('ProductListingComponent.productPriceLabel'),
    noProductsFoundTitle: t('ProductListingComponent.noProductsFoundTitle'),
    currency: authenticatedUser?.organization?.locale.currency.toUpperCase() ?? '',
    vat: tBundle('IndividualProductBundle.vat'),
    priceIncludingVat: tBundle('IndividualProductBundle.priceIncludingVat'),
    priceExcludingVat: tBundle('IndividualProductBundle.priceExcludingVat'),
    month: tBundle('IndividualProductBundle.month'),
    monthPeriod: tBundle('IndividualProductBundle.paymentMulti'),
    singlePayment: tBundle('IndividualProductBundle.paymentSingle'),
    textSearchTitle: t('ProductListingComponent.textSearchTitle'),
    textSearchPlaceholder: t('ProductListingComponent.textSearchPlaceholder'),
    filterResultsButton: t('ProductListingComponent.filterResultsButton'),
    showButton: t('ProductListingComponent.showButton'),
  };

  const handleItemClick = (item: WrapperItemOverview): void => {
    switch (item.type) {
      case 'BundleOverview':
        history.push(`/shop/bundle/${item.id}`);
        break;
      case 'SingleVariantProductOverview':
        history.push(`/shop/product/${item.id}`);
        break;
      case 'MultiVariantProductOverview':
        // MultiVariantProductOverview Needs more clarification in total
        history.push(`/shop/product/${item.id}`);
        break;
    }
  };

  const filterProducts = (filterParams: FilterProductsParams) => {
    const queryString = buildQueryString(filterParams);
    history.push(`/shop${queryString}`);
  };

  const debouncedFilterProducts = debounce(filterProducts, 500);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFilterProductsCallback = useCallback(debouncedFilterProducts, []);

  useEffect(() => {
    return () => {
      setIsMounted(false);
      debouncedFilterProducts.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setPagingOptions(prevPagingOptions => ({
      ...prevPagingOptions,
      currentPage: 1,
      numberOfProductsPerPage: NUMBER_OF_PRODUCT_PER_PAGE,
    }));
  }, [selectedBrandFilters, textSearch]);

  const handlePageChange = (pageChangeInfo: PageChangeInfo<WrapperItemOverview>) => {
    setLoading(true);
    setError('');
    const filterParams: FilterProductsParams = {
      paging: { ...pagingOptions, currentPage: pageChangeInfo.nextChosenPageNumber + 1 },
      brands: Object.keys(selectedBrandFilters),
      textSearch,
      sorting: sortingOptions.find(s => s.selected),
    };
    filterProducts(filterParams);
  };

  const handleSelectedBrandFiltersChange = (newSelectedBrandFilters: SelectedBrandFilters) => {
    setLoading(true);
    setError('');
    filterProducts({
      paging: pagingOptions,
      brands: Object.keys(newSelectedBrandFilters),
      textSearch,
      sorting: sortingOptions.find(s => s.selected),
    });
  };

  const handleSortingOptionsChange = (newSortingOptions: SortingOption[]) => {
    setLoading(true);
    setError('');
    filterProducts({
      paging: pagingOptions,
      brands: Object.keys(selectedBrandFilters),
      textSearch,
      sorting: newSortingOptions.find(s => s.selected),
    });
  };

  const handleTextSearchChange = (newTextSearch: string) => {
    setTextSearch(newTextSearch);
    setLoading(true);
    setError('');
    debouncedFilterProductsCallback({
      paging: pagingOptions,
      brands: Object.keys(selectedBrandFilters),
      textSearch: newTextSearch,
      sorting: sortingOptions.find(s => s.selected),
    });
  };

  if (isAdministratingSubsidiaryWithoutOrderRole) {
    return <AdministratingOtherOrganization />;
  }

  const isOnBehalfOrder = orderOnBehalfPendingUserSelection || orderOnBehalfUser.status || orderingForOrganization;
  const showOrderingOnBehalfInput = hasUserARequiredRole([UserRole.Orderer]) && isOnBehalfOrder;

  const renderProductList = () => {
    return (
      <>
        {showProductList && (
          <ProductListing
            products={products}
            loading={loading}
            numberOfProductColumns={numberOfProductColumns}
            onItemClick={(item: WrapperItemOverview) => handleItemClick(item)}
            errorText={error}
            translations={productListingTranslations}
            brandFilters={brandOptions}
            selectedBrandFilters={selectedBrandFilters}
            onSelectedBrandFiltersChange={handleSelectedBrandFiltersChange}
            sortingOptions={sortingOptions}
            onSortingOptionsChange={handleSortingOptionsChange}
            textSearch={textSearch}
            onTextSearchChange={handleTextSearchChange}
            mobileLayout={isMobile}
            locale={authenticatedUser?.organization?.locale.language}
          />
        )}
        {!loading && !error && showProductList ? (
          <>
            <Gutter size="lg" />
            <GridBag>
              {!isMobile && <GridBag.Item md={3}></GridBag.Item>}
              <GridBag.Item md={9}>
                <CustomPaginator
                  activatePageInfoObject={pageInfo}
                  onPageChange={handlePageChange}
                  paginatorStyleProps={{
                    paginationProps: {},
                  }}
                />
              </GridBag.Item>
            </GridBag>
          </>
        ) : (
          <BodyText>&nbsp;</BodyText>
        )}
      </>
    );
  };

  const renderWebshop = () => (
    <>
      {showProductList && orderOnBehalfUser.status && (
        <Flex alignRight>
          <Button onClick={() => history.push('/webshop')} variant="secondary">
            {tShop('externalWebshop.toShopButton')}
          </Button>
        </Flex>
      )}
      <Gutter size="layout-9" />
      <QuestionsAndAnswers />
    </>
  );

  return (
    <Page>
      <Gutter size="layout-8" />
      {showOrderingOnBehalfInput && (
        <GridBag>
          {!isMobile && <GridBag.Item sm={12} md={6} />}
          <GridBag.Item sm={12} md={6}>
            <OrderingOnBehalfInput />
          </GridBag.Item>
        </GridBag>
      )}
      {!deviceLimitFullInfoDismissed && isSingleDevicePolicyInEffectForUser('authenticated') && !isOnBehalfOrder && (
        <Div>
          <DeviceLimitFullNotification
            closeOnClick={() => {
              setDeviceLimitFullInfoDismissed(true);
            }}
          />
          <Gutter size="xlg" />
        </Div>
      )}

      {isWebshopEnabled(selectedOrganization, ownOrganization) ? renderWebshop() : renderProductList()}
    </Page>
  );
};

export default ProductList;
